import React, { useMemo, useState } from 'react';
import {
  Award,
  BadgeCheck,
  BookOpen,
  Briefcase,
  Building2,
  Database,
  ExternalLink,
  FileText,
  Gauge,
  GraduationCap,
  Info,
  Layers3,
  Languages,
  Radar,
  ShieldCheck,
  Target,
  UserRound,
  X
} from 'lucide-react';
import { formatCompensationRange } from '../../utils/compensation';

const asArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const dimensionLabel = (dimension) => {
  const key = String(typeof dimension === 'string' ? dimension : dimension?.key || dimension?.label || '').toLowerCase();
  const labels = {
    skills: 'Skills Evidence',
    experience: 'Experience Evidence',
    education: 'Education Context',
    location: 'Location And Work Model',
    salary: 'Compensation Fit',
    micro_points: 'Role-Specific Checks',
    micro_point: 'Role-Specific Checks'
  };
  return labels[key] || labelize(typeof dimension === 'string' ? dimension : dimension?.label || dimension?.key);
};

const scoreValue = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(100, numeric <= 1 ? numeric * 100 : numeric));
};

const percentText = (value) => {
  const numeric = scoreValue(value, null);
  return numeric === null ? null : `${Math.round(numeric)}%`;
};

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatMoney = (salary) => formatCompensationRange(salary);

const compactList = (value) => (
  asArray(value)
    .filter(Boolean)
    .map((item) => typeof item === 'string' ? labelize(item) : factText(item))
    .filter(Boolean)
    .join(' · ')
);

const humanizeDecisionText = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';

  if (/^No canonical skill requirements are available/i.test(text)) {
    return 'Specific skill requirements have not been clearly defined for this role, so skill fit needs a manual review.';
  }

  const yearsEvidence = text.match(/^Has (.+?) with ([\d.]+) years evidence\.?$/i);
  if (yearsEvidence) {
    const label = yearsEvidence[1];
    const years = Number(yearsEvidence[2]);
    if (!Number.isFinite(years) || years <= 0) {
      return `${label} is present in the evidence, but duration was not confirmed.`;
    }
    return `${label} is supported by ${years} ${years === 1 ? 'year' : 'years'} of evidence.`;
  }

  const simpleHas = text.match(/^Has (.+?)\.?$/i);
  if (simpleHas) return `${simpleHas[1]} is present in candidate evidence.`;

  const missingRequired = text.match(/^Missing required skill:\s*(.+)$/i);
  if (missingRequired) return `Verify required skill evidence: ${missingRequired[1]}.`;

  const missingPreferred = text.match(/^Missing preferred skill:\s*(.+)$/i);
  if (missingPreferred) return `Verify preferred skill evidence: ${missingPreferred[1]}.`;

  const missingEvidence = text.match(/^Missing required evidence:\s*(.+)$/i);
  if (missingEvidence) return `Verify required evidence: ${missingEvidence[1]}`;

  return text;
};

const factText = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return humanizeDecisionText(item);
  if (item.label || item.name || item.key) {
    const label = item.label || item.name || labelize(item.key);
    const score = item.score !== undefined && item.score !== null ? `${Math.round(scoreValue(item.score))}% fit` : null;
    const confidence = item.confidence !== undefined && item.confidence !== null ? `${Math.round(scoreValue(item.confidence))}% confidence` : null;
    return [label, [score, confidence].filter(Boolean).join(', '), humanizeDecisionText(item.explanation)].filter(Boolean).join(' - ');
  }
  return humanizeDecisionText(item.explanation || item.summary || item.label || item.name || item.key || item.title || item.question || JSON.stringify(item));
};

const answerText = (value) => {
  if (Array.isArray(value)) return value.map((item) => typeof item === 'string' ? labelize(item) : factText(item)).join(', ');
  if (value && typeof value === 'object') {
    return Object.entries(value)
      .map(([key, item]) => `${labelize(key)}: ${Array.isArray(item) ? item.map((entry) => typeof entry === 'string' ? labelize(entry) : formatEvidenceValue(entry)).filter(Boolean).join(', ') : formatEvidenceValue(item) || labelize(item)}`)
      .join(' · ');
  }
  return value === null || value === undefined ? '' : labelize(value);
};

const skillName = (skill) => (
  typeof skill === 'string'
    ? skill
    : skill?.skill_name || skill?.skillName || skill?.name || skill?.label || skill?.displayName || 'Skill'
);

const dateText = (start, end, isCurrent) => {
  const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });
  const format = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : formatter.format(date);
  };
  const startLabel = format(start);
  const endLabel = isCurrent ? 'Present' : format(end);
  return [startLabel, endLabel].filter(Boolean).join(' - ');
};

const sectionTitleClass = 'flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500';
const subtlePanelClass = 'rounded-lg border border-slate-200 bg-white p-4';

const dimensionDescription = (key) => {
  const normalized = String(key || '').toLowerCase();
  const descriptions = {
    skills: 'Required and preferred skills found in CV, profile, project, and confirmed skill evidence.',
    experience: 'Role history, seniority, years of experience, decision scope, and industry exposure.',
    education: 'Degree, field of study, academic strength, institution context, and related education evidence.',
    location: 'Location, remote fit, relocation preference, and work-readiness requirements.',
    salary: 'Expected compensation compared with the role budget or pay range.',
    micro_points: 'Portfolio proof, recent hands-on work, regulated exposure, stakeholder exposure, and other role-specific evidence checks.'
  };
  return descriptions[normalized] || 'Role requirement evidence compared with the candidate profile.';
};

const friendlyEvidenceKey = (key) => {
  const labels = {
    sourceText: 'Source note',
    values: 'Signals',
    decisionScope: 'Decision scope',
    requirementType: 'Requirement',
    minimumGpa: 'Minimum GPA',
    gpaScale: 'GPA scale',
    gradingSystem: 'Grading system',
    transcriptRequired: 'Transcript required',
    acceptsEquivalentExperience: 'Equivalent experience accepted',
    ageMonths: 'Evidence age',
    lastUsedDate: 'Last used',
    regulatedExposure: 'Regulated exposure',
    stakeholderGroups: 'Stakeholder exposure',
    projectName: 'Project',
    projectUrl: 'Project URL',
    repositoryUrl: 'Repository URL',
    budgetAmount: 'Budget amount',
    budgetCurrency: 'Budget currency',
    degreeClassification: 'Academic classification',
    classificationEquivalent: 'Classification equivalent',
    businessDomain: 'Business domain',
    impactScale: 'Impact scale',
    measurableImpact: 'Measurable impact',
    evidenceCount: 'Evidence count',
    verificationStatus: 'Verification status',
    credentialUrl: 'Verification URL',
    credentialId: 'Credential ID',
    subjectRank: 'Subject rank',
    countryRank: 'Country rank',
    continentRank: 'Continent rank',
    recognitionStatus: 'Recognition'
  };
  return labels[key] || labelize(key);
};

const formatEvidenceValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) return value.map(formatEvidenceValue).filter(Boolean).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([key, item]) => !['signalId', 'questionKey'].includes(key) && item !== null && item !== undefined && item !== '')
      .map(([key, item]) => {
        const formatted = formatEvidenceValue(item);
        return formatted ? `${friendlyEvidenceKey(key)}: ${formatted}` : null;
      })
      .filter(Boolean)
      .join(' · ');
  }
  return String(value);
};

const compactText = (value) => String(value || '').trim().replace(/\s+/g, ' ');

const safeExternalHref = (href) => {
  const text = String(href || '').trim();
  if (!text) return null;
  try {
    const parsed = new URL(text);
    if (!['http:', 'https:'].includes(parsed.protocol)) return null;
    parsed.hash = '';
    return parsed.toString();
  } catch (_error) {
    return null;
  }
};

const evidenceIdentity = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return compactText(item).toLowerCase();
  return compactText(item.explanation || item.label || item.name || item.key || item.title || JSON.stringify(item)).toLowerCase();
};

const uniqueByMeaning = (items) => {
  const seen = new Set();
  return asArray(items)
    .filter(Boolean)
    .filter((item) => {
      const key = evidenceIdentity(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const profileData = (profile) => {
  const data = profile?.profileData || profile?.profile_data;
  return data && typeof data === 'object' ? data : {};
};

const sourceLinksFor = (profile) => {
  const data = profileData(profile);
  const links = [
    ...(Array.isArray(data.trustedSources) ? data.trustedSources : []),
    profile?.evidenceSource || profile?.evidence_source,
    data.officialWebsite ? { provider: 'Official website', title: 'Official website', url: data.officialWebsite } : null,
    data.wikipediaUrl ? { provider: 'Wikipedia', title: 'Wikipedia profile', url: data.wikipediaUrl } : null
  ].filter((source) => source?.url);
  const seen = new Set();
  return links.filter((source) => {
    if (seen.has(source.url)) return false;
    seen.add(source.url);
    return true;
  });
};

const recognitionText = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'source_listed') return 'Found in trusted public sources';
  if (normalized === 'needs_review') return 'Needs manual verification';
  if (!normalized) return null;
  return labelize(status);
};

const confidenceText = (value) => {
  if (value === null || value === undefined) return null;
  return `${Math.round(scoreValue(value))}% source confidence`;
};

const SourceLinks = ({ profile }) => {
  const links = sourceLinksFor(profile)
    .map((source) => ({ ...source, url: safeExternalHref(source.url) }))
    .filter((source) => source.url);
  if (!links.length) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {links.map((source) => (
        <a
          key={source.url}
          href={source.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-sky-700 transition hover:border-sky-200 hover:bg-sky-50"
        >
          {source.provider || source.title || 'Source'} <ExternalLink size={11} />
        </a>
      ))}
    </div>
  );
};

const ProofLink = ({ href, label }) => {
  const safeHref = safeExternalHref(href);
  if (!safeHref) return null;
  return (
    <a
      href={safeHref}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-md bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700 transition hover:bg-sky-100"
    >
      {label} <ExternalLink size={11} />
    </a>
  );
};

const EvidenceDetails = ({ title, value }) => {
  const rows = value && typeof value === 'object' && !Array.isArray(value)
    ? Object.entries(value)
      .filter(([key, item]) => !['signalId', 'questionKey'].includes(key) && formatEvidenceValue(item))
    : [];

  if (!rows.length) {
    const text = formatEvidenceValue(value);
    if (!text) return null;
    return (
      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{title}</div>
        <div className="mt-1 text-xs leading-5 text-slate-600">{text}</div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{title}</div>
      <dl className="mt-2 space-y-1">
        {rows.map(([key, item]) => (
          <div key={key} className="text-xs leading-5">
            <dt className="font-semibold text-[#0b1f3a]">{friendlyEvidenceKey(key)}</dt>
            <dd className="break-words text-slate-600">{formatEvidenceValue(item)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const infoText = (item) => {
  if (item === null || item === undefined || item === '') return '';
  if (typeof item === 'string') return factText(item);
  if (typeof item !== 'object') return String(item);

  const label = item.label || item.name || item.title || item.skill_name || item.skillName || item.job_title || item.jobTitle || item.degree || item.degree_type || item.project_name || item.projectName || item.certification_name || item.certificationName || item.language_name || item.languageName || item.question || item.prompt || item.key;
  const detail = [
    item.company_name || item.companyName,
    item.institution || item.institution_name,
    item.field || item.field_of_study,
    item.proficiency_level || item.proficiencyLevel,
    item.years_of_experience !== undefined && item.years_of_experience !== null ? `${item.years_of_experience} yrs` : null,
    item.yearsExperience !== undefined && item.yearsExperience !== null ? `${item.yearsExperience} yrs` : null,
    item.explanation,
    item.summary,
    item.description,
    item.rawValue !== undefined ? formatEvidenceValue(item.rawValue) : null,
    item.answer_value !== undefined ? answerText(item.answer_value) : null,
    item.answerValue !== undefined ? answerText(item.answerValue) : null,
    item.score !== undefined && item.score !== null ? `${percentText(item.score)} score` : null,
    item.confidence !== undefined && item.confidence !== null ? `${percentText(item.confidence)} confidence` : null,
    item.confidence_score !== undefined && item.confidence_score !== null ? `${percentText(item.confidence_score)} confidence` : null,
    item.confidenceScore !== undefined && item.confidenceScore !== null ? `${percentText(item.confidenceScore)} confidence` : null
  ].filter(Boolean).join(' · ');

  if (label && detail) return `${label} - ${detail}`;
  if (label) return String(label);
  return formatEvidenceValue(item) || JSON.stringify(item);
};

const InfoButton = ({ title, description, groups = [] }) => {
  const [open, setOpen] = useState(false);
  const populatedGroups = groups
    .map((group) => ({
      ...group,
      items: asArray(group.items).map(infoText).filter(Boolean)
    }))
    .filter((group) => group.items.length || group.empty);

  if (!populatedGroups.length && !description) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700"
        aria-label={`More information about ${title}`}
      >
        <Info size={14} />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 px-4 py-6">
          <div className="max-h-[86vh] w-full max-w-3xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.22)]">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="font-display text-xl font-normal text-[#0b1f3a]">{title}</h3>
                {description && <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50"
                aria-label="Close information"
              >
                <X size={16} />
              </button>
            </div>
            <div className="max-h-[68vh] overflow-y-auto px-5 py-4">
              <div className="space-y-4">
                {populatedGroups.map((group) => (
                  <section key={group.title} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{group.title}</h4>
                      <span className="rounded-md bg-white px-2 py-1 text-xs font-semibold text-[#0b1f3a]">{formatNumber(group.items.length)}</span>
                    </div>
                    {group.items.length ? (
                      <ul className="mt-3 grid gap-2 md:grid-cols-2">
                        {group.items.map((item, index) => (
                          <li key={`${group.title}-${index}`} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm leading-6 text-slate-700">
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-3 text-sm leading-6 text-slate-500">{group.empty}</p>
                    )}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ScoreRing = ({ score, label = 'Overall match', size = 136 }) => {
  const numeric = Math.round(scoreValue(score));
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (numeric / 100) * circumference;
  const tone = numeric >= 80 ? '#059669' : numeric >= 65 ? '#0ea5e9' : numeric >= 50 ? '#d97706' : '#64748b';

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center text-center sm:text-left">
      <svg width={size} height={size} viewBox="0 0 136 136" className="shrink-0">
        <circle cx="68" cy="68" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="12" />
        <circle
          cx="68"
          cy="68"
          r={radius}
          fill="none"
          stroke={tone}
          strokeLinecap="round"
          strokeWidth="12"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 68 68)"
        />
        <text x="68" y="64" textAnchor="middle" className="fill-[#0b1f3a] text-[26px] font-semibold">{numeric}</text>
        <text x="68" y="84" textAnchor="middle" className="fill-slate-500 text-[11px] font-medium">percent</text>
      </svg>
      <div className="min-w-0">
        <div className="font-display text-xl font-normal text-[#0b1f3a]">{label}</div>
        <p className="mt-2 text-sm leading-6 text-slate-500">
          This score compares the role requirements with evidence from the candidate's skills, work history, education, projects, location, and compensation fit.
        </p>
      </div>
    </div>
  );
};

const DimensionBars = ({ dimensions }) => {
  const rows = asArray(dimensions);
  if (!rows.length) return <EmptyBlock label="No score breakdown is available yet." />;

  return (
    <div className="space-y-3">
      {rows.map((dimension) => {
        const score = scoreValue(dimension.score, null);
        const weight = Number(dimension.weightedContribution);
        return (
          <div key={dimension.key || dimension.label}>
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <span className="min-w-0 truncate text-xs font-medium text-slate-600">{dimensionLabel(dimension)}</span>
              <span className="shrink-0 text-xs font-semibold text-[#0b1f3a]">{score === null ? 'Not scored' : `${Math.round(score)}%`}</span>
            </div>
            <div className="grid grid-cols-[1fr_auto] items-center gap-2">
              <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-[#0ea5e9]" style={{ width: score === null ? '0%' : `${Math.max(4, score)}%` }} />
              </div>
              {Number.isFinite(weight) && <span className="w-14 text-right text-[11px] text-slate-400">{weight.toFixed(1)} impact</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RadarChart = ({ dimensions }) => {
  const points = asArray(dimensions).filter((dimension) => scoreValue(dimension.score, null) !== null);
  if (points.length < 3) return <DimensionBars dimensions={dimensions} />;

  const center = 96;
  const radius = 70;
  const polygon = points.map((dimension, index) => {
    const angle = ((Math.PI * 2) / points.length) * index - Math.PI / 2;
    const valueRadius = radius * (scoreValue(dimension.score) / 100);
    return `${center + Math.cos(angle) * valueRadius},${center + Math.sin(angle) * valueRadius}`;
  }).join(' ');

  const axis = points.map((dimension, index) => {
    const angle = ((Math.PI * 2) / points.length) * index - Math.PI / 2;
    const x = center + Math.cos(angle) * radius;
    const y = center + Math.sin(angle) * radius;
    const labelX = center + Math.cos(angle) * (radius + 20);
    const labelY = center + Math.sin(angle) * (radius + 20);
    return { dimension, x, y, labelX, labelY };
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)] lg:items-center">
      <svg viewBox="0 0 192 192" className="mx-auto h-56 w-56 max-w-full">
        {[0.25, 0.5, 0.75, 1].map((scale) => (
          <polygon
            key={scale}
            points={points.map((_, index) => {
              const angle = ((Math.PI * 2) / points.length) * index - Math.PI / 2;
              return `${center + Math.cos(angle) * radius * scale},${center + Math.sin(angle) * radius * scale}`;
            }).join(' ')}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth="1"
          />
        ))}
        {axis.map(({ dimension, x, y, labelX, labelY }) => (
          <g key={dimension.key || dimension.label}>
            <line x1={center} y1={center} x2={x} y2={y} stroke="#e2e8f0" strokeWidth="1" />
            <text x={labelX} y={labelY} textAnchor="middle" dominantBaseline="middle" className="fill-slate-500 text-[8px] font-medium">
              {dimensionLabel(dimension)}
            </text>
          </g>
        ))}
        <polygon points={polygon} fill="#0ea5e9" fillOpacity="0.18" stroke="#0284c7" strokeWidth="2" />
        {polygon.split(' ').map((point, index) => {
          const [x, y] = point.split(',');
          return <circle key={index} cx={x} cy={y} r="3" fill="#0284c7" />;
        })}
      </svg>
      <DimensionBars dimensions={dimensions} />
    </div>
  );
};

const DataDepthChart = ({ dataDepth = {}, candidate = {} }) => {
  const rows = [
    ['Skills', dataDepth.skills ?? asArray(candidate.skills).length, Target],
    ['Experience', dataDepth.experience ?? asArray(candidate.experience).length, Briefcase],
    ['Education', dataDepth.education ?? asArray(candidate.education).length, GraduationCap],
    ['Certifications', dataDepth.certifications ?? asArray(candidate.certifications).length, Award],
    ['Projects', dataDepth.projects ?? asArray(candidate.projects).length, Layers3],
    ['Languages', dataDepth.languages ?? asArray(candidate.languages).length, Languages],
    ['Research', dataDepth.researchOutputs ?? asArray(candidate.researchOutputs).length, BookOpen],
    ['Evidence checks', dataDepth.evidenceSignals ?? asArray(candidate.profileSignals).length, Radar],
    ['Profile signals', dataDepth.profileSignals ?? asArray(candidate.profileSignals).length, Gauge],
    ['Source evidence', dataDepth.sourceEvidence ?? 0, FileText],
    ['Candidate answers', dataDepth.profileAnswers ?? asArray(candidate.profileAnswerHighlights).length, BookOpen]
  ];
  const max = Math.max(...rows.map(([, count]) => Number(count) || 0), 1);

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {rows.map(([label, count, Icon]) => {
        const numeric = Number(count) || 0;
        return (
          <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-2 text-xs font-medium text-slate-600"><Icon size={14} /> {label}</span>
              <span className="text-sm font-semibold text-[#0b1f3a]">{formatNumber(numeric)}</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white">
              <div className="h-full rounded-full bg-[#0f766e]" style={{ width: `${Math.max(5, (numeric / max) * 100)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const EvidenceCoveragePanel = ({ coverage }) => {
  if (!coverage) return null;
  const score = scoreValue(coverage.overallScore ?? coverage.overall_score, null);
  const sectionScores = coverage.sectionScores || coverage.section_scores || {};
  const counts = coverage.evidenceCounts || coverage.evidence_counts || {};
  const infoGroups = [
    {
      title: 'Profile areas',
      items: Object.entries(sectionScores).map(([key, value]) => `${labelize(key)} - ${Math.round(scoreValue(value))}% complete`)
    },
    {
      title: 'Evidence counts',
      items: Object.entries(counts).map(([key, value]) => `${labelize(key)} - ${formatNumber(value)} item${Number(value) === 1 ? '' : 's'}`)
    }
  ];

  return (
    <div className={subtlePanelClass}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className={sectionTitleClass}>
            <ShieldCheck size={14} /> Candidate information quality
            <InfoButton
              title="Candidate information quality"
              description="Shows how complete and well-supported each candidate profile area is for hiring review."
              groups={infoGroups}
            />
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
            This shows how much of the candidate profile is source-backed enough to support employer decisions and scoring.
          </p>
        </div>
        {score !== null && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-right">
            <div className="text-xl font-semibold text-[#0b1f3a]">{Math.round(score)}%</div>
            <div className="text-xs text-slate-500">information completeness</div>
          </div>
        )}
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {Object.entries(sectionScores).map(([key, value]) => (
          <div key={key} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-slate-600">{labelize(key)}</span>
              <span className="text-xs font-semibold text-[#0b1f3a]">{Math.round(scoreValue(value))}%</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white">
              <div className="h-full rounded-full bg-[#0f766e]" style={{ width: `${Math.max(5, scoreValue(value))}%` }} />
            </div>
          </div>
        ))}
      </div>
      {Object.keys(counts).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(counts).map(([key, value]) => (
            <span key={key} className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600">
              {labelize(key)}: <span className="font-semibold text-[#0b1f3a]">{formatNumber(value)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const EmptyBlock = ({ label }) => (
  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-7 text-center text-sm text-slate-500">
    {label}
  </div>
);

const SignalCard = ({ signal }) => {
  const candidateScore = scoreValue(signal.candidateScore ?? signal.score, null);
  const requiredScore = scoreValue(signal.requiredScore, null);
  const passed = signal.passed !== false;
  const candidateProof = signal.evidence?.candidateRawValue || signal.evidence?.candidate?.rawValue || signal.evidence?.candidate || signal.evidence?.contribution?.candidateEvidence;
  const roleExpectation = signal.evidence?.job?.signalValue || signal.evidence?.contribution?.jobEvidence?.signalValue || signal.evidence?.job;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="min-w-0 truncate text-sm font-semibold text-[#0b1f3a]">{signal.label || labelize(signal.key)}</div>
          <div className="mt-1 text-xs text-slate-500">{labelize(signal.category || 'Evidence')}</div>
        </div>
        <span className={`shrink-0 rounded-md px-2 py-1 text-xs font-semibold ${passed ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
          {passed ? 'Meets target' : 'Review'}
        </span>
      </div>
      {(candidateScore !== null || requiredScore !== null) && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Candidate evidence</span>
            <span className="font-semibold text-[#0b1f3a]">{candidateScore !== null ? Math.round(candidateScore) : '-'}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-[#0ea5e9]" style={{ width: `${Math.max(4, candidateScore || 0)}%` }} />
          </div>
          {requiredScore !== null && <div className="text-[11px] text-slate-400">Employer target: {Math.round(requiredScore)}</div>}
        </div>
      )}
      {signal.explanation && <p className="mt-3 text-xs leading-5 text-slate-600">{signal.explanation}</p>}
      {(candidateProof || roleExpectation) && (
        <div className="mt-3 grid gap-2">
          <EvidenceDetails title="Candidate proof" value={candidateProof} />
          <EvidenceDetails title="Role expectation" value={roleExpectation} />
        </div>
      )}
    </div>
  );
};

const DecisionColumn = ({ title, items, tone, empty }) => {
  const toneClasses = {
    green: {
      shell: 'border-emerald-100 bg-emerald-50',
      title: 'text-emerald-700',
      item: 'border-emerald-100 bg-white text-emerald-950',
      dot: 'bg-emerald-500'
    },
    amber: {
      shell: 'border-amber-100 bg-amber-50',
      title: 'text-amber-700',
      item: 'border-amber-100 bg-white text-amber-950',
      dot: 'bg-amber-500'
    },
    sky: {
      shell: 'border-sky-100 bg-sky-50',
      title: 'text-sky-700',
      item: 'border-sky-100 bg-white text-sky-950',
      dot: 'bg-sky-500'
    }
  }[tone] || {
    shell: 'border-slate-200 bg-slate-50',
    title: 'text-slate-600',
    item: 'border-slate-200 bg-white text-slate-700',
    dot: 'bg-slate-400'
  };

  return (
    <div className={`rounded-lg border p-4 ${toneClasses.shell}`}>
      <div className={`text-xs font-semibold uppercase tracking-[0.12em] ${toneClasses.title}`}>{title}</div>
      {items.length ? (
        <div className="mt-3 space-y-2">
          {items.map((item, index) => (
            <div key={index} className={`flex gap-2 rounded-lg border px-3 py-2 text-sm leading-6 ${toneClasses.item}`}>
              <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${toneClasses.dot}`} />
              <span>{factText(item)}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm leading-6 text-slate-600">{empty}</p>
      )}
    </div>
  );
};

const DecisionBrief = ({ strengths, risks, supportingEvidence }) => {
  const allStrengths = uniqueByMeaning(strengths.length ? strengths : supportingEvidence);
  const allRisks = uniqueByMeaning(risks);
  const supportingRows = uniqueByMeaning(supportingEvidence);
  if (!allStrengths.length && !allRisks.length && !supportingRows.length) return null;

  return (
    <div className={subtlePanelClass}>
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <div className={sectionTitleClass}><BadgeCheck size={14} /> Employer decision brief</div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
            A hiring-first readout of what supports movement and what should be verified before taking action.
          </p>
        </div>
        {/* {hasSkillVerification && (
          <span className="rounded-md bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
            Refresh match if CV or job profile changed
          </span>
        )} */}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <DecisionColumn
          title="Advance signals"
          tone="green"
          items={allStrengths}
          empty="The score is positive, but supporting evidence is not available yet."
        />
        <DecisionColumn
          title="Verification queue"
          tone="amber"
          items={allRisks}
          empty="No material review gaps are currently flagged by the matching engine."
        />
      </div>

      {strengths.length > 0 && supportingRows.length > 0 && (
        <div className="mt-4">
          <DecisionColumn
            title="Requirement evidence"
            tone="sky"
            items={supportingRows}
            empty="No supporting requirement facts are available."
          />
        </div>
      )}
    </div>
  );
};

const SkillsEvidence = ({ skills, matchedSkills }) => {
  const skillRows = asArray(skills);
  const matched = asArray(matchedSkills);
  const primary = skillRows.length ? skillRows : matched.map((skill) => ({ name: skill }));

  if (!primary.length) return <EmptyBlock label="No skill evidence is available yet." />;

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {primary.map((skill, index) => {
        const years = skill?.years_of_experience ?? skill?.yearsExperience;
        const confidence = skill?.confidence_score ?? skill?.confidenceScore;
        return (
          <div key={`${skillName(skill)}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="min-w-0 truncate text-sm font-semibold text-[#0b1f3a]">{skillName(skill)}</div>
                <div className="mt-1 text-xs text-slate-500">
                  {[labelize(skill?.skill_category || skill?.category || ''), skill?.proficiency_level || skill?.proficiencyLevel, years ? `${years} yrs` : null].filter((item) => item && item !== 'Not Set').join(' · ')}
                </div>
              </div>
              {confidence !== undefined && confidence !== null && <span className="rounded-md bg-white px-2 py-1 text-[11px] font-semibold text-slate-600">{percentText(confidence)}</span>}
            </div>
            {(skill?.evidence_type || skill?.evidenceType || skill?.source) && (
              <div className="mt-2 text-xs leading-5 text-slate-500">
                {[skill.evidence_type || skill.evidenceType, skill.source].filter(Boolean).map(labelize).join(' · ')}
              </div>
            )}
            {(skill?.last_used_date || skill?.lastUsedDate || skill?.portfolio_evidence_url || skill?.portfolioEvidenceUrl) && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(skill.last_used_date || skill.lastUsedDate) && <span className="rounded-md bg-white px-2 py-1 text-xs text-slate-600">Last used {String(skill.last_used_date || skill.lastUsedDate).slice(0, 10)}</span>}
                <ProofLink href={skill.portfolio_evidence_url || skill.portfolioEvidenceUrl} label="Skill proof" />
              </div>
            )}
            {skill?.evidence && <EvidenceDetails title="Skill evidence" value={skill.evidence} />}
          </div>
        );
      })}
    </div>
  );
};

const EducationEvidenceCard = ({ item, index }) => {
  const institutionProfile = item.institutionProfile || item.institution_profile;
  const programProfile = item.programProfile || item.program_profile;
  const educationVerification = item.educationVerification || item.education_verification;
  const publicMentions = asArray(educationVerification?.publicMentions || educationVerification?.public_mentions);
  const accreditationRecords = asArray(educationVerification?.accreditationRecords || educationVerification?.accreditation_records);
  const relatedPublicSignals = asArray(
    educationVerification?.evidence?.relatedPublicSignals ||
    educationVerification?.evidence?.related_public_signals
  );
  const institutionData = profileData(institutionProfile);
  const programData = profileData(programProfile);
  const institutionName = item.institution || item.institution_name || 'Institution not set';
  const programName = programProfile?.program_name || programProfile?.programName || [item.degree || item.degree_type, item.field || item.field_of_study].filter(Boolean).join(' - ');
  const sourceSummary = institutionData.summary || programData.summary;
  const sourceNotes = programData.sourceNotes || institutionData.sourceNotes;
  const recognition = recognitionText(programProfile?.recognition_status || institutionProfile?.recognition_status);
  const confidence = confidenceText(programProfile?.confidence_score || programProfile?.confidenceScore || institutionProfile?.confidence_score || institutionProfile?.confidenceScore);

  return (
    <div key={`${institutionName}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#0b1f3a]">
            <Building2 size={16} className="shrink-0 text-sky-700" />
            <span className="min-w-0 truncate">{institutionName}</span>
          </div>
          <div className="mt-1 text-sm leading-6 text-slate-600">{programName || 'Education record'}</div>
          <div className="mt-1 text-xs leading-5 text-slate-500">
            {[item.institutionCountry || item.institution_country || institutionProfile?.country, item.institutionRegion || item.institution_region, item.graduation_date ? `Graduated ${new Date(item.graduation_date).getFullYear()}` : null].filter(Boolean).join(' · ')}
          </div>
        </div>
        {(recognition || confidence) && (
          <div className="shrink-0 rounded-lg border border-emerald-100 bg-white px-3 py-2 text-xs leading-5 text-emerald-800">
            {recognition && <div className="font-semibold">{recognition}</div>}
            {confidence && <div>{confidence}</div>}
          </div>
        )}
      </div>

      {sourceSummary && <p className="mt-3 text-sm leading-6 text-slate-600">{sourceSummary}</p>}

      <div className="mt-3 grid gap-2 md:grid-cols-2">
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
          <span className="font-semibold text-[#0b1f3a]">Candidate academic claim:</span>{' '}
          {[item.degree || item.degree_type, item.field || item.field_of_study, item.degreeClassification || item.degree_classification, item.classificationEquivalent || item.classification_equivalent, item.gpa ? `GPA ${item.gpa}${item.gpa_scale || item.gpaScale ? `/${item.gpa_scale || item.gpaScale}` : ''}` : null, item.grading_system || item.gradingSystem].filter(Boolean).join(' · ') || 'Education record captured'}
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
          <span className="font-semibold text-[#0b1f3a]">Institution evidence:</span>{' '}
          {[recognition, programProfile?.countryRank || programProfile?.country_rank ? `country rank #${programProfile.countryRank || programProfile.country_rank}` : null, programProfile?.subjectRank || programProfile?.subject_rank ? `subject rank #${programProfile.subjectRank || programProfile.subject_rank}` : null, institutionProfile?.globalRank || institutionProfile?.global_rank ? `global rank #${institutionProfile.globalRank || institutionProfile.global_rank}` : null].filter(Boolean).join(' · ') || 'Trusted-source profile available; rankings only shown when sourced'}
        </div>
      </div>

      {educationVerification && (
        <div className="mt-3 rounded-lg border border-sky-100 bg-white p-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">Public education verification</div>
              <div className="mt-1 text-xs leading-5 text-slate-600">
                {[
                  labelize(educationVerification.verificationStatus || educationVerification.verification_status),
                  labelize(educationVerification.identityMatchStatus || educationVerification.identity_match_status),
                  labelize(educationVerification.programMatchStatus || educationVerification.program_match_status),
                  labelize(educationVerification.graduationMatchStatus || educationVerification.graduation_match_status)
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(educationVerification.verificationScore || educationVerification.verification_score) && (
                <span className="rounded-md bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700">
                  {Math.round(scoreValue(educationVerification.verificationScore || educationVerification.verification_score))}% verification
                </span>
              )}
              {(educationVerification.confidenceScore || educationVerification.confidence_score) && (
                <span className="rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600">
                  {Math.round(scoreValue(educationVerification.confidenceScore || educationVerification.confidence_score))}% confidence
                </span>
              )}
            </div>
          </div>

          {(educationVerification.gpaPublicStatus === 'public_value_found' || educationVerification.gpa_public_status === 'public_value_found') && (
            <div className="mt-2 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
              Public GPA evidence: {educationVerification.publicGpa || educationVerification.public_gpa}
              {(educationVerification.publicGpaScale || educationVerification.public_gpa_scale) ? `/${educationVerification.publicGpaScale || educationVerification.public_gpa_scale}` : ''}
            </div>
          )}

          {publicMentions.length > 0 && (
            <div className="mt-3 space-y-2">
              {publicMentions.slice(0, 3).map((mention, mentionIndex) => (
                <div key={`${mention.source_url || mention.sourceUrl || mentionIndex}`} className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                  <div className="font-semibold text-[#0b1f3a]">
                    {[labelize(mention.mention_type || mention.mentionType), mention.identity_confidence || mention.identityConfidence ? `${Math.round(scoreValue(mention.identity_confidence || mention.identityConfidence))}% identity confidence` : null].filter(Boolean).join(' · ')}
                  </div>
                  {(mention.source_snippet || mention.sourceSnippet) && <div className="mt-1">{mention.source_snippet || mention.sourceSnippet}</div>}
                  <ProofLink href={mention.source_url || mention.sourceUrl || mention.evidence_source?.url || mention.evidenceSource?.url} label="Public source" />
                </div>
              ))}
            </div>
          )}

          {accreditationRecords.length > 0 && (
            <div className="mt-3 space-y-2">
              {accreditationRecords.slice(0, 3).map((record, recordIndex) => (
                <div key={`${record.source_url || record.sourceUrl || recordIndex}`} className="rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs leading-5 text-emerald-800">
                  <div className="font-semibold">
                    {[record.accreditor_name || record.accreditorName || 'Accreditation source', labelize(record.status), record.confidence_score || record.confidenceScore ? `${Math.round(scoreValue(record.confidence_score || record.confidenceScore))}% confidence` : null].filter(Boolean).join(' · ')}
                  </div>
                  {(record.evidence?.snippet || record.evidence?.sourceSnippet) && <div className="mt-1">{record.evidence.snippet || record.evidence.sourceSnippet}</div>}
                  <ProofLink href={record.source_url || record.sourceUrl || record.evidence_source?.url || record.evidenceSource?.url} label="Accreditation source" />
                </div>
              ))}
            </div>
          )}

          {relatedPublicSignals.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Related public candidate signals</div>
              {relatedPublicSignals.slice(0, 4).map((signal, signalIndex) => (
                <div key={`${signal.sourceUrl || signal.source_url || signalIndex}`} className="rounded-md border border-violet-100 bg-violet-50 px-3 py-2 text-xs leading-5 text-violet-900">
                  <div className="font-semibold text-[#0b1f3a]">
                    {[
                      labelize(signal.signalType || signal.signal_type || 'Public signal'),
                      signal.confidenceScore || signal.confidence_score ? `${Math.round(scoreValue(signal.confidenceScore || signal.confidence_score))}% confidence` : null
                    ].filter(Boolean).join(' · ')}
                  </div>
                  {(signal.sourceSnippet || signal.source_snippet) && <div className="mt-1">{signal.sourceSnippet || signal.source_snippet}</div>}
                  <ProofLink href={signal.sourceUrl || signal.source_url} label="Public source" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(compactList(item.honors_awards || item.honorsAwards) || compactList(item.relevant_coursework || item.relevantCoursework) || item.transcript_evidence_url || item.transcriptEvidenceUrl) && (
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          {compactList(item.honors_awards || item.honorsAwards) && (
            <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
              <span className="font-semibold text-[#0b1f3a]">Honors and awards:</span> {compactList(item.honors_awards || item.honorsAwards)}
            </div>
          )}
          {compactList(item.relevant_coursework || item.relevantCoursework) && (
            <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
              <span className="font-semibold text-[#0b1f3a]">Relevant coursework:</span> {compactList(item.relevant_coursework || item.relevantCoursework)}
            </div>
          )}
          <ProofLink href={item.transcript_evidence_url || item.transcriptEvidenceUrl} label="Transcript evidence" />
        </div>
      )}
      {item.evidence && <div className="mt-3"><EvidenceDetails title="Education source evidence" value={item.evidence} /></div>}
      {sourceNotes && <p className="mt-2 text-xs leading-5 text-slate-500">{sourceNotes}</p>}
      <SourceLinks profile={programProfile || institutionProfile} />
    </div>
  );
};

const CertificationEvidenceCard = ({ item, index }) => {
  const check = item.credential_check || item.credentialCheck || {};
  const issuerProfile = check.issuer_profile || check.issuerProfile || item.issuer_profile || item.issuerProfile;
  const issuerData = profileData(issuerProfile);
  const issuerRecognition = recognitionText(issuerProfile?.recognition_status || issuerProfile?.recognitionStatus);
  const verificationStatus = recognitionText(check.verification_status || check.verificationStatus) || labelize(check.verification_status || check.verificationStatus || 'unverified');
  const proofScore = check.score !== undefined && check.score !== null ? `${Math.round(scoreValue(check.score))}% proof strength` : null;
  const credentialAssessment = item.credentialAssessment || {};
  const assessmentStatus = credentialAssessment.status;
  const assessmentTone = assessmentStatus === 'verified'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : assessmentStatus === 'unverified_claim'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-slate-200 bg-white text-slate-700';

  return (
    <div key={`${item.certification_name || item.certificationName || index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#0b1f3a]">{item.certification_name || item.certificationName || 'Certification'}</div>
          <div className="mt-1 text-xs leading-5 text-slate-500">
            {[item.issuing_organization || item.issuingOrganization, item.expiry_date || item.expiryDate ? `expires ${String(item.expiry_date || item.expiryDate).slice(0, 10)}` : null].filter(Boolean).join(' · ')}
          </div>
        </div>
        <span className={`shrink-0 rounded-md border px-2 py-1 text-xs font-semibold ${assessmentTone}`}>
          {credentialAssessment.label || verificationStatus}
        </span>
      </div>
      {credentialAssessment.employerGuidance && (
        <p className="mt-2 text-xs leading-5 text-slate-600">{credentialAssessment.employerGuidance}</p>
      )}
      {(proofScore || issuerRecognition) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {proofScore && <span className="rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">{proofScore}</span>}
          {issuerRecognition && <span className="rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-600">{issuerRecognition}</span>}
          {issuerProfile?.professional_body && <span className="rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-600">Professional body</span>}
        </div>
      )}
      {(issuerData.summary || issuerData.sourceNotes) && (
        <p className="mt-2 text-xs leading-5 text-slate-600">{issuerData.summary || issuerData.sourceNotes}</p>
      )}
      {(item.credential_url || item.credentialUrl || item.credential_id || item.credentialId) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ProofLink href={item.credential_url || item.credentialUrl} label="Credential proof" />
          {(item.credential_id || item.credentialId) && <span className="rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-700">Credential ID: {item.credential_id || item.credentialId}</span>}
        </div>
      )}
      {item.evidence && <div className="mt-2"><EvidenceDetails title="Credential evidence" value={item.evidence} /></div>}
      {check.evidence && <div className="mt-2"><EvidenceDetails title="Verification evidence" value={check.evidence} /></div>}
      <SourceLinks profile={issuerProfile} />
    </div>
  );
};

const CompanyContextDetails = ({ item }) => {
  const check = item.companyContextCheck || item.company_context_check;
  const companyProfile = check?.companyProfile || check?.company_profile;
  const companyData = profileData(companyProfile);
  if (!check && !companyProfile) return null;

  const status = check?.contextStatus || check?.context_status || companyProfile?.recognitionStatus || companyProfile?.recognition_status;
  const score = check?.companyContextScore || check?.company_context_score;
  const confidence = check?.confidenceScore || check?.confidence_score || companyProfile?.confidenceScore || companyProfile?.confidence_score;
  const companySize = companyProfile?.companySize || companyProfile?.company_size || companyData.companySize;
  const revenueContext = companyProfile?.revenueContext || companyProfile?.revenue_context || companyData.revenueContext;
  const prestigeTier = companyProfile?.prestigeTier || companyProfile?.prestige_tier || companyData.prestigeTier;
  const industry = companyProfile?.industry || companyData.industry;
  const country = companyProfile?.country || companyData.country;
  const summary = companyData.summary || companyData.sourceNotes;

  return (
    <div className="mt-2 rounded-lg border border-violet-100 bg-violet-50 p-3 text-xs leading-5 text-violet-900">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="font-semibold text-[#0b1f3a]">Public company context</div>
          <div className="mt-1 text-violet-800">
            {[labelize(status), industry, country].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {score && <span className="rounded-md bg-white px-2 py-1 font-semibold text-violet-700">{Math.round(scoreValue(score))}% context</span>}
          {confidence && <span className="rounded-md bg-white px-2 py-1 font-medium text-slate-600">{Math.round(scoreValue(confidence))}% confidence</span>}
        </div>
      </div>
      {[companySize, revenueContext, prestigeTier ? labelize(prestigeTier) : null].filter(Boolean).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {[companySize, revenueContext, prestigeTier ? labelize(prestigeTier) : null].filter(Boolean).map((value, index) => (
            <span key={index} className="rounded-md bg-white px-2 py-1 text-xs font-medium text-violet-800">{value}</span>
          ))}
        </div>
      )}
      {summary && <p className="mt-2 text-xs leading-5 text-violet-900">{summary}</p>}
      <SourceLinks profile={companyProfile} />
    </div>
  );
};

const ProfileEvidence = ({ candidateEvidence = {}, candidate = {} }) => {
  const experience = asArray(candidateEvidence.experience || candidate.experience);
  const education = asArray(candidateEvidence.education || candidate.education);
  const projects = asArray(candidateEvidence.projects || candidate.projects);
  const certifications = asArray(candidateEvidence.certifications || candidate.certifications);
  const languages = asArray(candidateEvidence.languages || candidate.languages);
  const profileAnswers = asArray(candidateEvidence.profileAnswerHighlights || candidate.profileAnswerHighlights);
  const tagHighlights = asArray(candidateEvidence.tagHighlights || candidate.tagHighlights);
  const currentRole = candidateEvidence.currentRole || candidate.currentRole;
  const currentRoleTitle = compactText(currentRole?.job_title || currentRole?.jobTitle).toLowerCase();
  const currentRoleCompany = compactText(currentRole?.company_name || currentRole?.companyName).toLowerCase();
  const currentRoleAlreadyInExperience = currentRole && experience.some((item) => (
    compactText(item.job_title || item.jobTitle).toLowerCase() === currentRoleTitle &&
    compactText(item.company_name || item.companyName).toLowerCase() === currentRoleCompany
  ));

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <div className={subtlePanelClass}>
        <div className={sectionTitleClass}><Briefcase size={14} /> Experience proof</div>
        {currentRole && !currentRoleAlreadyInExperience && (
          <div className="mt-3 rounded-lg bg-slate-50 p-3">
            <div className="text-sm font-semibold text-[#0b1f3a]">{currentRole.job_title || currentRole.jobTitle || 'Current role'}</div>
            <div className="mt-1 text-xs leading-5 text-slate-500">
              {[currentRole.company_name || currentRole.companyName, currentRole.industry, currentRole.location].filter(Boolean).join(' · ')}
            </div>
          </div>
        )}
        {experience.length ? (
          <div className="mt-3 space-y-3">
            {experience.map((item, index) => (
              <div key={`${item.id || item.company_name || index}`} className="border-t border-slate-100 pt-3 first:border-0 first:pt-0">
                <div className="text-sm font-semibold text-[#0b1f3a]">{item.job_title || item.jobTitle || 'Role'}{item.company_name || item.companyName ? ` · ${item.company_name || item.companyName}` : ''}</div>
                <div className="mt-1 text-xs leading-5 text-slate-500">
                  {[dateText(item.start_date || item.startDate, item.end_date || item.endDate, item.is_current_job || item.isCurrentJob), labelize(item.seniority_level || item.seniorityLevel || ''), labelize(item.decision_scope || item.decisionScope || '')].filter((value) => value && value !== 'Not Set').join(' · ')}
                </div>
                {[item.team_size || item.teamSize ? `Team ${item.team_size || item.teamSize}` : null, item.budget_responsibility_amount || item.budgetResponsibilityAmount ? `Budget ${item.budget_currency || item.budgetCurrency || ''} ${formatNumber(item.budget_responsibility_amount || item.budgetResponsibilityAmount)}` : null, item.company_size || item.companySize, item.revenue_context || item.revenueContext].filter(Boolean).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {[item.team_size || item.teamSize ? `Team ${item.team_size || item.teamSize}` : null, item.budget_responsibility_amount || item.budgetResponsibilityAmount ? `Budget ${item.budget_currency || item.budgetCurrency || ''} ${formatNumber(item.budget_responsibility_amount || item.budgetResponsibilityAmount)}` : null, item.company_size || item.companySize, item.revenue_context || item.revenueContext].filter(Boolean).map((value, valueIndex) => (
                      <span key={valueIndex} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">{value}</span>
                    ))}
                  </div>
                )}
                {compactList(item.stakeholder_groups || item.stakeholderGroups, 5) && <div className="mt-1 text-xs leading-5 text-slate-500"><span className="font-semibold text-[#0b1f3a]">Stakeholders:</span> {compactList(item.stakeholder_groups || item.stakeholderGroups, 5)}</div>}
                {compactList(item.regulated_exposure || item.regulatedExposure, 5) && <div className="mt-1 text-xs leading-5 text-slate-500"><span className="font-semibold text-[#0b1f3a]">Regulated exposure:</span> {compactList(item.regulated_exposure || item.regulatedExposure, 5)}</div>}
                {compactList(item.key_achievements || item.keyAchievements, 2) && <p className="mt-1 text-xs leading-5 text-slate-600">{compactList(item.key_achievements || item.keyAchievements, 2)}</p>}
                {item.job_description || item.jobDescription ? <p className="mt-2 text-xs leading-5 text-slate-600">{item.job_description || item.jobDescription}</p> : null}
                {compactList(item.technologies_used || item.technologiesUsed) && <div className="mt-1 text-xs leading-5 text-sky-700"><span className="font-semibold text-[#0b1f3a]">Tools and technologies:</span> {compactList(item.technologies_used || item.technologiesUsed)}</div>}
                {compactList(item.skills_gained || item.skillsGained) && <div className="mt-1 text-xs leading-5 text-slate-500"><span className="font-semibold text-[#0b1f3a]">Skills gained:</span> {compactList(item.skills_gained || item.skillsGained)}</div>}
                {(item.evidence || item.evidence_confidence || item.evidenceConfidence || item.source) && (
                  <div className="mt-2 grid gap-2">
                    {item.evidence && <EvidenceDetails title="Experience evidence" value={item.evidence} />}
                    {(item.evidence_confidence || item.evidenceConfidence || item.source) && (
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                        {[item.evidence_confidence || item.evidenceConfidence ? `Evidence confidence ${percentText(item.evidence_confidence || item.evidenceConfidence)}` : null, item.source ? `Source ${labelize(item.source)}` : null].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                )}
                <CompanyContextDetails item={item} />
              </div>
            ))}
          </div>
        ) : !currentRole ? <div className="mt-3"><EmptyBlock label="No experience trail is available yet." /></div> : null}
      </div>

      <div className={subtlePanelClass}>
        <div className={sectionTitleClass}><GraduationCap size={14} /> Education and institution context</div>
        {education.length ? (
          <div className="mt-3 space-y-3">
            {education.map((item, index) => <EducationEvidenceCard key={`${item.institution || item.institution_name || index}`} item={item} index={index} />)}
          </div>
        ) : <div className="mt-3"><EmptyBlock label="No education context is available yet." /></div>}
      </div>

      <div className={subtlePanelClass}>
        <div className={sectionTitleClass}><Layers3 size={14} /> Project and portfolio evidence</div>
        {projects.length ? (
          <div className="mt-3 space-y-3">
            {projects.map((item, index) => (
              <div key={`${item.project_name || item.projectName || index}`} className="rounded-lg bg-slate-50 p-3">
                <div className="text-sm font-semibold text-[#0b1f3a]">{item.project_name || item.projectName || item.role || 'Project'}</div>
                {item.description && <p className="mt-1 text-xs leading-5 text-slate-600">{item.description}</p>}
                {[item.business_domain || item.businessDomain, item.impact_scale || item.impactScale, item.role].filter(Boolean).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {[item.business_domain || item.businessDomain, item.impact_scale || item.impactScale, item.role].filter(Boolean).map((value, valueIndex) => (
                      <span key={valueIndex} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600">{value}</span>
                    ))}
                  </div>
                )}
                {compactList(item.technologies || item.skills_demonstrated || item.skillsDemonstrated, 5) && (
                  <div className="mt-2 text-xs leading-5 text-sky-700">{compactList(item.technologies || item.skills_demonstrated || item.skillsDemonstrated, 5)}</div>
                )}
                {compactList(item.outcomes, 3) && <div className="mt-1 text-xs leading-5 text-slate-500">{compactList(item.outcomes, 3)}</div>}
                {Object.keys(item.measurable_impact || item.measurableImpact || {}).length > 0 && (
                  <div className="mt-2 text-xs leading-5 text-slate-600">
                    <span className="font-semibold text-[#0b1f3a]">Measured impact:</span> {formatEvidenceValue(item.measurable_impact || item.measurableImpact)}
                  </div>
                )}
                {(item.research_methods || item.researchMethods) && (
                  <div className="mt-1 text-xs leading-5 text-slate-500"><span className="font-semibold text-[#0b1f3a]">Methods:</span> {compactList(item.research_methods || item.researchMethods)}</div>
                )}
                {(item.project_url || item.projectUrl || item.repository_url || item.repositoryUrl || item.confidence_score || item.confidenceScore || item.source) && (
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <ProofLink href={item.project_url || item.projectUrl} label="Project proof" />
                    <ProofLink href={item.repository_url || item.repositoryUrl} label="Repository proof" />
                    {(item.confidence_score || item.confidenceScore) && <span className="rounded-md bg-white px-2 py-1 text-xs text-slate-600">Evidence confidence {percentText(item.confidence_score || item.confidenceScore)}</span>}
                    {item.source && <span className="rounded-md bg-white px-2 py-1 text-xs text-slate-600">Source {labelize(item.source)}</span>}
                  </div>
                )}
                {item.evidence && <div className="mt-2"><EvidenceDetails title="Project evidence" value={item.evidence} /></div>}
              </div>
            ))}
          </div>
        ) : <div className="mt-3"><EmptyBlock label="No project evidence is available yet." /></div>}
      </div>

      <div className={subtlePanelClass}>
        <div className={sectionTitleClass}>
          <Award size={14} /> Certifications, language, evidence themes
          <InfoButton
            title="Certifications, language, evidence themes"
            description="Additional proof points that support credential strength, language readiness, and candidate evidence themes."
            groups={[
              { title: 'Certifications', items: certifications },
              { title: 'Languages', items: languages },
              { title: 'Evidence themes', items: tagHighlights }
            ]}
          />
        </div>
        <div className="mt-3 grid gap-3">
          {certifications.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-[#0b1f3a]">Certifications</div>
              <div className="mt-2 grid gap-2">
                {certifications.map((item, index) => <CertificationEvidenceCard key={`${item.certification_name || item.certificationName || index}`} item={item} index={index} />)}
              </div>
            </div>
          )}
          {languages.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0b1f3a]"><Languages size={13} /> Languages</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {languages.map((item, index) => (
                  <span key={`${item.language_name || item.languageName || index}`} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
                    {[item.language_name || item.languageName || item.language || 'Language', item.proficiency_label || item.proficiencyLabel, item.cefr_level || item.cefrLevel, item.test_name || item.testName, item.test_score || item.testScore, item.professional_usage || item.professionalUsage ? 'Professional use' : null, item.years_used || item.yearsUsed ? `${item.years_used || item.yearsUsed} yrs` : null].filter(Boolean).join(' · ')}
                  </span>
                ))}
              </div>
              {languages.some((item) => item.evidence || item.professional_context || item.professionalContext || item.source) && (
                <div className="mt-2 grid gap-2">
                  {languages.map((item, index) => (
                    (item.evidence || item.professional_context || item.professionalContext || item.source) ? (
                      <div key={`${item.language_name || item.languageName || index}-evidence`} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                        <div className="font-semibold text-[#0b1f3a]">{item.language_name || item.languageName || item.language || 'Language'}</div>
                        {item.professional_context || item.professionalContext ? <div>{item.professional_context || item.professionalContext}</div> : null}
                        {item.evidence && <EvidenceDetails title="Language evidence" value={item.evidence} />}
                        {item.source && <div>Source {labelize(item.source)}</div>}
                      </div>
                    ) : null
                  ))}
                </div>
              )}
            </div>
          )}
          {tagHighlights.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-[#0b1f3a]">Evidence themes</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {tagHighlights.map((tag, index) => (
                  <span key={`${tag.label || tag.core_name || index}`} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600">
                    {[tag.label || tag.display_name || tag.displayName || tag.core_name || tag.coreName, tag.category_type || tag.categoryType ? labelize(tag.category_type || tag.categoryType) : null, tag.proficiency_level || tag.proficiencyLevel, tag.years_evidence || tag.yearsEvidence ? `${tag.years_evidence || tag.yearsEvidence} yrs` : null, tag.confidence_score || tag.confidenceScore ? `${percentText(tag.confidence_score || tag.confidenceScore)} confidence` : null].filter(Boolean).join(' · ')}
                  </span>
                ))}
              </div>
              {tagHighlights.some((tag) => tag.evidence || tag.source) && (
                <div className="mt-2 grid gap-2">
                  {tagHighlights.map((tag, index) => (
                    (tag.evidence || tag.source) ? (
                      <div key={`${tag.label || tag.core_name || index}-evidence`} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                        <div className="font-semibold text-[#0b1f3a]">{tag.label || tag.display_name || tag.displayName || tag.core_name || tag.coreName || 'Evidence theme'}</div>
                        {tag.source && <div>Source {labelize(tag.source)}</div>}
                        {tag.evidence && <EvidenceDetails title="Theme evidence" value={tag.evidence} />}
                      </div>
                    ) : null
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {profileAnswers.length > 0 && (
        <div className={`${subtlePanelClass} xl:col-span-2`}>
          <div className={sectionTitleClass}>
            <BookOpen size={14} /> Profile answer context
            <InfoButton
              title="Profile answer context"
              description="Candidate questionnaire answers used as qualitative context alongside CV and structured profile evidence."
              groups={[{ title: 'Candidate profile answers', items: profileAnswers }]}
            />
          </div>
          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            {profileAnswers.map((item, index) => (
              <div key={`${item.question_key || item.questionKey || index}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-sm font-semibold text-[#0b1f3a]">{item.prompt || labelize(item.question_key || item.questionKey)}</div>
                <div className="mt-2 text-sm leading-6 text-slate-600">{answerText(item.answer_value ?? item.answerValue)}</div>
                {(item.category || item.section || item.source || item.answered_at || item.answeredAt) && (
                  <div className="mt-2 text-xs leading-5 text-slate-400">
                    {[item.category ? labelize(item.category) : null, item.section ? labelize(item.section) : null, item.source ? `Source ${labelize(item.source)}` : null, item.answered_at || item.answeredAt ? `Answered ${String(item.answered_at || item.answeredAt).slice(0, 10)}` : null].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ResearchEvidencePanel = ({ researchOutputs }) => {
  const rows = asArray(researchOutputs);
  if (!rows.length) return null;

  return (
    <div className={subtlePanelClass}>
      <div className={sectionTitleClass}><BookOpen size={14} /> Research and publications</div>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        {rows.map((item, index) => (
          <div key={`${item.id || item.title || item.output_type || index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-sm font-semibold text-[#0b1f3a]">{item.title || item.output_type || item.outputType || 'Research output'}</div>
            <div className="mt-1 text-xs leading-5 text-slate-500">
              {[item.output_type || item.outputType, item.publisher_or_institution || item.publisherOrInstitution, item.publication_date || item.publicationDate ? String(item.publication_date || item.publicationDate).slice(0, 10) : null].filter(Boolean).map((value) => typeof value === 'string' ? value : labelize(value)).join(' · ')}
            </div>
            {item.description && <p className="mt-2 text-xs leading-5 text-slate-600">{item.description}</p>}
            {compactList(item.methods) && <div className="mt-2 text-xs leading-5 text-slate-500"><span className="font-semibold text-[#0b1f3a]">Methods:</span> {compactList(item.methods)}</div>}
            <div className="mt-2 flex flex-wrap gap-1.5">
              <ProofLink href={item.url} label="Research source" />
              {item.doi && <span className="rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-700">DOI: {item.doi}</span>}
              {(item.confidence_score || item.confidenceScore) && <span className="rounded-md bg-white px-2 py-1 text-xs text-slate-600">Evidence confidence {percentText(item.confidence_score || item.confidenceScore)}</span>}
              {item.source && <span className="rounded-md bg-white px-2 py-1 text-xs text-slate-600">Source {labelize(item.source)}</span>}
            </div>
            {item.evidence && <div className="mt-2"><EvidenceDetails title="Research evidence" value={item.evidence} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const fieldEvidenceLabel = (row) => {
  const fieldPath = row.field_path || row.fieldPath;
  if (!fieldPath) return labelize(row.source_table || row.sourceTable || 'Captured evidence');
  const parts = String(fieldPath).split('.').filter(Boolean);
  return labelize(parts.slice(-2).join(' '));
};

const FieldEvidencePanel = ({ fieldEvidence }) => {
  const rows = asArray(fieldEvidence);
  if (!rows.length) return null;

  return (
    <div className={subtlePanelClass}>
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <div className={sectionTitleClass}><FileText size={14} /> Source-backed extraction evidence</div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
            These are the specific CV/profile facts that were captured and used as evidence. Contact evidence remains locked until mutual interest.
          </p>
        </div>
        <span className="inline-flex h-8 items-center rounded-md bg-slate-50 px-2.5 text-xs font-semibold text-slate-600">
          {rows.length} evidence item{rows.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {rows.map((row, index) => {
          const value = row.extracted_value ?? row.extractedValue;
          const sourceText = row.source_text || row.sourceText;
          const metadata = row.metadata && typeof row.metadata === 'object' ? row.metadata : null;
          return (
            <div key={`${row.id || row.field_path || row.fieldPath || index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0b1f3a]">{fieldEvidenceLabel(row)}</div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">
                    {[row.source_table || row.sourceTable ? labelize(row.source_table || row.sourceTable) : null, row.page_number || row.pageNumber ? `Page ${row.page_number || row.pageNumber}` : null, row.extraction_method || row.extractionMethod ? `${labelize(row.extraction_method || row.extractionMethod)} capture` : null].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {(row.confidence_score || row.confidenceScore || row.confirmed !== undefined) && (
                  <div className="shrink-0 rounded-md bg-white px-2 py-1 text-xs text-slate-600">
                    {[row.confidence_score || row.confidenceScore ? `${percentText(row.confidence_score || row.confidenceScore)} confidence` : null, row.confirmed === true ? 'Confirmed' : row.confirmed === false ? 'Not confirmed' : null].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              {value !== null && value !== undefined && value !== '' && (
                <div className="mt-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
                  <span className="font-semibold text-[#0b1f3a]">Captured value:</span> {formatEvidenceValue(value)}
                </div>
              )}
              {sourceText && (
                <div className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
                  <span className="font-semibold text-[#0b1f3a]">Source text:</span> {sourceText}
                </div>
              )}
              {metadata && <div className="mt-2"><EvidenceDetails title="Additional context" value={metadata} /></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ProfileSignalEvidencePanel = ({ profileSignals }) => {
  const rows = asArray(profileSignals);
  if (!rows.length) return null;

  return (
    <div className={subtlePanelClass}>
      <div className={sectionTitleClass}><Gauge size={14} /> Profile evidence signals</div>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        {rows.map((signal, index) => {
          const evidenceItems = asArray(signal.evidence_items || signal.evidenceItems);
          return (
            <div key={`${signal.key || signal.label || index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-sm font-semibold text-[#0b1f3a]">{signal.label || labelize(signal.key)}</div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">
                    {[signal.category ? labelize(signal.category) : null, signal.subcategory ? labelize(signal.subcategory) : null, signal.evidence_count || signal.evidenceCount ? `${signal.evidence_count || signal.evidenceCount} supporting detail${Number(signal.evidence_count || signal.evidenceCount) === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div className="shrink-0 rounded-md bg-white px-2 py-1 text-xs text-slate-600">
                  {[signal.average_score || signal.averageScore ? `${percentText(signal.average_score || signal.averageScore)} strength` : null, signal.average_confidence || signal.averageConfidence ? `${percentText(signal.average_confidence || signal.averageConfidence)} confidence` : null].filter(Boolean).join(' · ') || 'Evidence captured'}
                </div>
              </div>
              {signal.sample_explanation || signal.sampleExplanation ? <p className="mt-2 text-xs leading-5 text-slate-600">{signal.sample_explanation || signal.sampleExplanation}</p> : null}
              {evidenceItems.length > 0 && (
                <div className="mt-3 space-y-2">
                  {evidenceItems.map((item, itemIndex) => (
                    <div key={itemIndex} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
                      <div className="font-semibold text-[#0b1f3a]">
                        {[item.score !== undefined && item.score !== null ? `${percentText(item.score)} evidence` : null, item.confidence !== undefined && item.confidence !== null ? `${percentText(item.confidence)} confidence` : null, item.source ? `Source ${labelize(item.source)}` : null].filter(Boolean).join(' · ') || 'Supporting detail'}
                      </div>
                      {item.explanation && <div className="mt-1">{item.explanation}</div>}
                      {item.rawValue !== undefined && item.rawValue !== null && <EvidenceDetails title="Captured value" value={item.rawValue} />}
                      {item.evidence && <EvidenceDetails title="Evidence" value={item.evidence} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PreferencesCvPanel = ({ preferences = {}, candidate = {}, candidateSnapshot = {}, cvSummary = {} }) => {
  const salary = candidate.salaryExpectations || {};
  const cvRows = [
    ['Relevant experience', candidateSnapshot.relevantExperienceYears !== undefined && candidateSnapshot.relevantExperienceYears !== null ? `${candidateSnapshot.relevantExperienceYears} years` : null],
    ['Seniority', candidateSnapshot.seniorityLevel || cvSummary.seniority_level || cvSummary.seniorityLevel],
    ['CV summary confidence', candidateSnapshot.cvSummaryConfidence !== undefined && candidateSnapshot.cvSummaryConfidence !== null ? percentText(candidateSnapshot.cvSummaryConfidence) : cvSummary.confidence_score || cvSummary.confidenceScore ? percentText(cvSummary.confidence_score || cvSummary.confidenceScore) : null],
    ['Successful extractions', candidateSnapshot.cvSuccessfulExtractions],
    ['Failed extractions', candidateSnapshot.cvFailedExtractions],
    ['Experience total', cvSummary.experience_years_total || cvSummary.experienceYearsTotal],
    ['Experience relevant', cvSummary.experience_years_relevant || cvSummary.experienceYearsRelevant],
    ['Expected pay', formatMoney(salary)],
    ['Remote preferred', salary.isRemotePreferred === undefined || salary.isRemotePreferred === null ? null : salary.isRemotePreferred ? 'Yes' : 'No']
  ].filter(([, value]) => value !== null && value !== undefined && value !== '');

  const preferenceRows = [
    ['Work authorization', preferences.workAuthorizationStatus ? labelize(preferences.workAuthorizationStatus) : null],
    ['Authorization regions', compactList(preferences.workAuthorizationRegions)],
    ['Relocation', preferences.willingToRelocate === undefined || preferences.willingToRelocate === null ? null : preferences.willingToRelocate ? 'Open' : 'Not preferred'],
    ['Work mode', preferences.preferredWorkType ? labelize(preferences.preferredWorkType) : null],
    ['Notice period', preferences.noticePeriodDays !== null && preferences.noticePeriodDays !== undefined ? `${preferences.noticePeriodDays} days` : null],
    ['Preferred locations', compactList(salary.preferredLocations || preferences.preferredLocations)],
    ['Preferred industries', compactList(preferences.preferredIndustries)],
    ['Preferred job types', compactList(preferences.preferredJobTypes)],
    ['Job fields of interest', compactList(preferences.jobFieldsOfInterest)]
  ].filter(([, value]) => value);

  const hasCvNarrative = cvSummary.display_summary || cvSummary.displaySummary || cvSummary.career_progression || cvSummary.careerProgression || cvSummary.education_summary || cvSummary.educationSummary || cvSummary.skill_matrix || cvSummary.skillMatrix || compactList(cvSummary.key_achievements || cvSummary.keyAchievements);
  if (!cvRows.length && !preferenceRows.length && !hasCvNarrative) return null;

  return (
    <div className={subtlePanelClass}>
      <div className={sectionTitleClass}><UserRound size={14} /> Candidate preferences and CV evidence</div>
      {(preferenceRows.length > 0 || cvRows.length > 0) && (
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {[...preferenceRows, ...cvRows].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
              <div className="mt-1 break-words text-sm font-semibold text-[#0b1f3a]">{value}</div>
            </div>
          ))}
        </div>
      )}
      {(cvSummary.display_summary || cvSummary.displaySummary || cvSummary.career_progression || cvSummary.careerProgression || cvSummary.education_summary || cvSummary.educationSummary) && (
        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          {(cvSummary.display_summary || cvSummary.displaySummary) && <EvidenceDetails title="CV summary" value={cvSummary.display_summary || cvSummary.displaySummary} />}
          {(cvSummary.career_progression || cvSummary.careerProgression) && <EvidenceDetails title="Career progression" value={cvSummary.career_progression || cvSummary.careerProgression} />}
          {(cvSummary.education_summary || cvSummary.educationSummary) && <EvidenceDetails title="Education summary" value={cvSummary.education_summary || cvSummary.educationSummary} />}
        </div>
      )}
      {compactList(cvSummary.key_achievements || cvSummary.keyAchievements) && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-600">
          <span className="font-semibold text-[#0b1f3a]">CV highlights:</span> {compactList(cvSummary.key_achievements || cvSummary.keyAchievements)}
        </div>
      )}
      {(cvSummary.skill_matrix || cvSummary.skillMatrix) && (
        <div className="mt-4">
          <EvidenceDetails title="Skill matrix" value={cvSummary.skill_matrix || cvSummary.skillMatrix} />
        </div>
      )}
    </div>
  );
};

const CandidateFactsStrip = ({ candidate = {}, candidateEvidence = {}, candidateSnapshot = {} }) => {
  const preferences = candidateEvidence.preferences || candidate.preferences || {};
  const currentRole = candidateEvidence.currentRole || candidate.currentRole || {};
  const facts = [
    ['Current location', candidate.location],
    ['Experience level', candidate.experienceLevel ? labelize(candidate.experienceLevel) : null],
    ['Experience', candidate.experienceYears !== null && candidate.experienceYears !== undefined ? `${candidate.experienceYears} years` : null],
    ['Availability', candidate.availability ? labelize(candidate.availability) : null],
    ['Expected pay', formatMoney(candidate.salaryExpectations)],
    ['Work mode', preferences.preferredWorkType ? labelize(preferences.preferredWorkType) : candidate.profile?.preferredWorkType ? labelize(candidate.profile.preferredWorkType) : null],
    ['Relocation', preferences.willingToRelocate === undefined || preferences.willingToRelocate === null ? null : preferences.willingToRelocate ? 'Open' : 'Not preferred'],
    ['Work authorization', preferences.workAuthorizationStatus ? labelize(preferences.workAuthorizationStatus) : null],
    ['Onboarding completion', candidateSnapshot.profileCompletionPercentage !== undefined ? percentText(candidateSnapshot.profileCompletionPercentage) : candidate.profile?.completionPercentage !== undefined ? percentText(candidate.profile.completionPercentage) : null],
    ['CV reviewed', candidateSnapshot.cvFilename || candidate.cv?.filename],
    ['Recent role', currentRole.job_title || currentRole.jobTitle],
    ['Current industry', currentRole.industry]
  ].filter(([, value]) => value);

  if (!facts.length) return null;

  return (
    <div className={subtlePanelClass}>
      <div className={sectionTitleClass}><UserRound size={14} /> Candidate facts used in this match</div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {facts.map(([label, value]) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
            <div className="mt-1 break-words text-sm font-semibold text-[#0b1f3a]">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EvaluationReview = ({ dimensions, risks, infoGroups = [] }) => {
  const dimensionRows = asArray(dimensions);
  if (!dimensionRows.length) return null;

  return (
    <div className={subtlePanelClass}>
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <div className={sectionTitleClass}>
            <ShieldCheck size={14} /> How we evaluated this candidate
            <InfoButton
              title="How this candidate was evaluated"
              description="Reviewed points are the hiring areas, requirements, and verification items used to calculate this match."
              groups={infoGroups}
            />
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
            Each area below shows what was checked, how strongly the candidate matched it, and where the employer should take a closer look before expressing interest.
          </p>
        </div>
        {asArray(risks).length > 0 && (
          <span className="inline-flex h-8 items-center rounded-md bg-amber-50 px-2.5 text-xs font-semibold text-amber-700">
            {asArray(risks).length} review point{asArray(risks).length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
        <div className="hidden grid-cols-[180px_110px_minmax(0,1fr)] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 md:grid">
          <div>Hiring area</div>
          <div>Fit</div>
          <div>What was checked</div>
        </div>
        {dimensionRows.map((dimension) => (
          <div key={dimension.key || dimension.label} className="grid gap-2 border-b border-slate-100 px-4 py-3 last:border-0 md:grid-cols-[180px_110px_minmax(0,1fr)] md:gap-3 md:items-center">
            <div>
              <div className="text-sm font-semibold text-[#0b1f3a]">{dimensionLabel(dimension)}</div>
              {Number.isFinite(Number(dimension.weightedContribution)) && <div className="mt-1 text-xs text-slate-400">Score impact {Number(dimension.weightedContribution).toFixed(1)}</div>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#0b1f3a]">{percentText(dimension.score) || 'Not scored'}</span>
              <div className="h-2 w-16 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-[#0ea5e9]" style={{ width: scoreValue(dimension.score, null) === null ? '0%' : `${Math.max(5, scoreValue(dimension.score))}%` }} />
              </div>
            </div>
            <div className="text-sm leading-6 text-slate-600">{dimensionDescription(dimension.key || dimension.label)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MatchIntelligencePanel = ({ match, candidate: profileCandidate, compact = false }) => {
  const insights = match?.insights || {};
  const candidate = useMemo(() => ({
    ...(profileCandidate || {}),
    ...(match?.candidate || {})
  }), [match, profileCandidate]);

  const profileDetails = candidate.profileDetails || {};
  const baseCandidateEvidence = insights.candidateEvidence || {};
  const candidateEvidence = {
    ...baseCandidateEvidence,
    skills: asArray(profileDetails.skills).length ? profileDetails.skills : baseCandidateEvidence.skills,
    experience: asArray(profileDetails.experience).length ? profileDetails.experience : baseCandidateEvidence.experience,
    education: asArray(profileDetails.education).length ? profileDetails.education : baseCandidateEvidence.education,
    certifications: asArray(profileDetails.certifications).length ? profileDetails.certifications : baseCandidateEvidence.certifications,
    projects: asArray(profileDetails.projects).length ? profileDetails.projects : baseCandidateEvidence.projects,
    languages: asArray(profileDetails.languages).length ? profileDetails.languages : baseCandidateEvidence.languages,
    researchOutputs: asArray(profileDetails.researchOutputs).length ? profileDetails.researchOutputs : baseCandidateEvidence.researchOutputs,
    profileAnswerHighlights: asArray(profileDetails.profileAnswers).length ? profileDetails.profileAnswers : baseCandidateEvidence.profileAnswerHighlights,
    evidenceCoverage: asArray(profileDetails.evidenceCoverage).length ? profileDetails.evidenceCoverage[0] : baseCandidateEvidence.evidenceCoverage,
    fieldEvidence: asArray(profileDetails.fieldEvidence).length ? profileDetails.fieldEvidence : baseCandidateEvidence.fieldEvidence,
    profileSignals: asArray(profileDetails.profileSignals).length ? profileDetails.profileSignals : baseCandidateEvidence.profileSignals,
    tagHighlights: asArray(profileDetails.tagHighlights).length ? profileDetails.tagHighlights : baseCandidateEvidence.tagHighlights
  };
  const dimensions = asArray(insights.dimensions || insights.topDimensions).length
    ? asArray(insights.dimensions || insights.topDimensions)
    : Object.entries(match?.scoreBreakdown?.dimensions || match?.scoreBreakdown || {})
      .filter(([, value]) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)))
      .map(([key, value]) => ({ key, label: labelize(key), score: value }));
  const overallScore = match?.overallMatchScore || match?.score || 0;
  const evidenceSignals = asArray(insights.evidenceSignals || match?.microPoints);
  const requirementEvidence = asArray(insights.requirementEvidence || match?.explanation?.matchedFacts);
  const risks = asArray(insights.reviewRisks || match?.explanation?.gaps)
    .concat(asArray(match?.missingSkills), asArray(match?.missingQualifications))
    .filter(Boolean);
  const strengths = asArray(match?.explanation?.strengths);
  const matchedSkills = asArray(match?.matchingSkills);
  const candidateSnapshot = insights.candidateSnapshot || {};
  const cvSummary = candidateEvidence.cvSummary || candidate.cvSummary || {};
  const skills = asArray(candidateEvidence.skills || candidate.skills);
  const evidenceCoverage = candidateEvidence.evidenceCoverage || candidate.evidenceCoverage || asArray(candidate.profileDetails?.evidenceCoverage)[0];
  const researchOutputs = asArray(candidateEvidence.researchOutputs || candidate.researchOutputs);
  const fieldEvidence = asArray(candidateEvidence.fieldEvidence);
  const profileSignals = asArray(candidateEvidence.profileSignals || candidate.profileSignals);
  const preferences = candidateEvidence.preferences || candidate.preferences || {};
  const dataDepth = {
    ...(insights.dataDepth || {}),
    skills: skills.length,
    experience: asArray(candidateEvidence.experience || candidate.experience).length,
    education: asArray(candidateEvidence.education || candidate.education).length,
    certifications: asArray(candidateEvidence.certifications || candidate.certifications).length,
    projects: asArray(candidateEvidence.projects || candidate.projects).length,
    languages: asArray(candidateEvidence.languages || candidate.languages).length,
    researchOutputs: researchOutputs.length,
    evidenceSignals: evidenceSignals.length,
    profileSignals: profileSignals.length,
    profileAnswers: asArray(candidateEvidence.profileAnswerHighlights || candidate.profileAnswerHighlights).length,
    sourceEvidence: fieldEvidence.length
  };
  const experience = asArray(candidateEvidence.experience || candidate.experience);
  const education = asArray(candidateEvidence.education || candidate.education);
  const certifications = asArray(candidateEvidence.certifications || candidate.certifications);
  const projects = asArray(candidateEvidence.projects || candidate.projects);
  const languages = asArray(candidateEvidence.languages || candidate.languages);
  const profileAnswers = asArray(candidateEvidence.profileAnswerHighlights || candidate.profileAnswerHighlights);
  const evidenceInfoGroups = [
    { title: 'Skills', items: skills },
    { title: 'Experience', items: experience },
    { title: 'Education', items: education },
    { title: 'Certifications', items: certifications },
    { title: 'Projects', items: projects },
    { title: 'Languages', items: languages },
    { title: 'Research', items: researchOutputs },
    { title: 'Profile signals', items: profileSignals },
    { title: 'Candidate answers', items: profileAnswers },
    { title: 'Source evidence', items: fieldEvidence },
    { title: 'Role-specific checks', items: evidenceSignals }
  ];
  const evaluationInfoGroups = [
    {
      title: 'Reviewed hiring areas',
      items: dimensions.map((dimension) => `${dimensionLabel(dimension)} - ${percentText(dimension.score) || 'not scored'}. ${dimensionDescription(dimension.key || dimension.label)}`)
    },
    {
      title: 'Review points',
      items: risks,
      empty: 'No review points are flagged for this candidate.'
    }
  ];
  const roleRequirementInfoGroups = [
    { title: 'Matched requirements', items: matchedSkills },
    { title: 'Candidate skill evidence', items: skills }
  ];
  const roleCheckInfoGroups = [
    {
      title: 'Role-specific checks',
      items: evidenceSignals.map((signal) => `${signal.label || labelize(signal.key)} - ${signal.passed === false ? 'Review' : 'Meets target'}${signal.candidateScore !== undefined ? ` · candidate ${signal.candidateScore}` : ''}${signal.requiredScore !== undefined ? ` / target ${signal.requiredScore}` : ''}${signal.explanation ? ` · ${signal.explanation}` : ''}`),
      empty: 'No role-specific checks are available for this candidate.'
    }
  ];
  const fitAreaInfoGroups = [
    {
      title: 'Fit by hiring area',
      items: dimensions.map((dimension) => `${dimensionLabel(dimension)} - ${percentText(dimension.score) || 'not scored'}. ${dimensionDescription(dimension.key || dimension.label)}`)
    }
  ];

  return (
    <div className="space-y-5">
      <div className={`${subtlePanelClass} max-w-md`}>
        <ScoreRing score={overallScore} />
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="font-semibold text-[#0b1f3a]">{match?.matchBand || '-'}</div>
            <div className="mt-1 text-slate-500">Match band</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="font-semibold text-[#0b1f3a]">{labelize(match?.matchStatus || 'potential')}</div>
            <div className="mt-1 text-slate-500">Workflow state</div>
          </div>
        </div>
      </div>

      {(match?.explanation?.summary || candidateSnapshot.summary || candidate.summary || cvSummary.summary || cvSummary.profile_summary) && (
        <div className={subtlePanelClass}>
          <div className={sectionTitleClass}><FileText size={14} /> Profile summary</div>
          {match?.explanation?.summary && <p className="mt-3 text-sm leading-6 text-[#0b1f3a]">{humanizeDecisionText(match.explanation.summary)}</p>}
          {(candidateSnapshot.summary || candidate.summary) && <p className="mt-2 text-sm leading-6 text-slate-600">{candidateSnapshot.summary || candidate.summary}</p>}
          {(cvSummary.summary || cvSummary.profile_summary) && <p className="mt-2 text-sm leading-6 text-slate-500">{cvSummary.summary || cvSummary.profile_summary}</p>}
        </div>
      )}

      <DecisionBrief
        strengths={strengths}
        risks={risks}
        supportingEvidence={requirementEvidence.length ? requirementEvidence : matchedSkills}
      />

      <CandidateFactsStrip candidate={candidate} candidateEvidence={candidateEvidence} candidateSnapshot={candidateSnapshot} />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className={subtlePanelClass}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className={sectionTitleClass}>
              <Radar size={14} /> Fit by hiring area
              <InfoButton
                title="Fit by hiring area"
                description="Each area shows how the candidate scored against a major employer decision dimension."
                groups={fitAreaInfoGroups}
              />
            </div>
            <span className="rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-500">{dimensions.length} areas reviewed</span>
          </div>
          <RadarChart dimensions={dimensions} />
        </div>

        <div className={subtlePanelClass}>
          <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <div className={sectionTitleClass}>
                <Database size={14} /> Evidence reviewed
                <InfoButton
                  title="Evidence reviewed"
                  description="Candidate information used to explain this match, grouped by source area."
                  groups={evidenceInfoGroups}
                />
              </div>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
                Candidate information available for this hiring review.
              </p>
            </div>
            {(candidateSnapshot.cvDataQuality || candidateSnapshot.profileCompletionPercentage !== undefined || candidateSnapshot.cvTextExtractionConfidence !== undefined) && (
              <div className="flex flex-wrap gap-2 text-xs">
                {candidateSnapshot.profileCompletionPercentage !== undefined && <span className="rounded-md bg-slate-50 px-2.5 py-1 font-medium text-slate-600">Onboarding {percentText(candidateSnapshot.profileCompletionPercentage)}</span>}
                {candidateSnapshot.cvDataQuality && <span className="rounded-md bg-slate-50 px-2.5 py-1 font-medium text-slate-600">CV {labelize(candidateSnapshot.cvDataQuality)}</span>}
                {candidateSnapshot.cvTextExtractionConfidence !== undefined && <span className="rounded-md bg-slate-50 px-2.5 py-1 font-medium text-slate-600">Extraction {percentText(candidateSnapshot.cvTextExtractionConfidence)}</span>}
              </div>
            )}
          </div>
          <DataDepthChart dataDepth={dataDepth} candidate={candidate} />
        </div>
      </div>

      <EvaluationReview
        dimensions={dimensions}
        risks={risks}
        infoGroups={evaluationInfoGroups}
      />

      <EvidenceCoveragePanel coverage={evidenceCoverage} />

      <div className={subtlePanelClass}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className={sectionTitleClass}>
            <Target size={14} /> Role requirements met
            <InfoButton
              title="Role requirements met"
              description="Candidate skill evidence and stored requirement matches used to support employer-side shortlisting."
              groups={roleRequirementInfoGroups}
            />
          </div>
        </div>
        <SkillsEvidence skills={skills} matchedSkills={matchedSkills} />
      </div>

      <div className={subtlePanelClass}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className={sectionTitleClass}>
            <Gauge size={14} /> Role-specific checks
            <InfoButton
              title="Role-specific checks"
              description="Fine-grained checks generated for this role, including pass/review state and candidate evidence against employer targets."
              groups={roleCheckInfoGroups}
            />
          </div>
        </div>
        {evidenceSignals.length ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {evidenceSignals.map((signal, index) => <SignalCard key={`${signal.key || signal.label || index}`} signal={signal} />)}
          </div>
        ) : <EmptyBlock label="No role-specific checks are available yet." />}
      </div>

      {!compact && <ProfileEvidence candidateEvidence={candidateEvidence} candidate={candidate} />}
      {!compact && <ProfileSignalEvidencePanel profileSignals={profileSignals} />}
      {!compact && <ResearchEvidencePanel researchOutputs={researchOutputs} />}
      {!compact && <PreferencesCvPanel preferences={preferences} candidate={candidate} candidateSnapshot={candidateSnapshot} cvSummary={cvSummary} />}
      {!compact && <FieldEvidencePanel fieldEvidence={fieldEvidence} />}
    </div>
  );
};

export default MatchIntelligencePanel;
